import axios from 'axios';
import io from 'socket.io-client';
import { app as appConfig, socket as socketConfig } from '@/jsx/config/index.js';

/**
 * React外のページでチャットの未読状態をリアルタイム管理
 * Node.jsサーバーからのSocket通知を受信してDOM更新
 */
class ChatUnreadChecker {
    constructor(socketUrl, unreadCheckUrl) {
        this.socketUrl = socketUrl;
        this.unreadCheckUrl = unreadCheckUrl;
        this.socket = null;
    }

    /**
     * 未読チェック機能を開始
     * - 初期状態をLaravel DBから取得
     * - Socket接続を確立して通知を受信待機
     */
    async start() {
        try {
            // 初期状態を取得（ページロード時のみ）
            await this.checkUnreadStatus();

            // JWTトークンを取得してSocket接続を確立
            await this.connectSocket();

            // 接続中に取りこぼした通知があっても、DBの最新状態で再同期
            await this.checkUnreadStatus();

            console.log('Chat unread checker started');
        } catch (error) {
            console.error('Failed to start unread checker:', error);
        }
    }

    /**
     * Node.jsサーバーへSocket接続
     */
    async connectSocket() {
        try {
            // チャット用JWTトークンを取得
            const response = await axios.get(appConfig.path.authToken, {
                withCredentials: true,
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            const token = response.data.token;

            if (!token) {
                throw new Error('Failed to get JWT token');
            }

            // Socket.IO接続を確立（JWTトークン付き）
            this.socket = io(this.socketUrl, {
                transports: ['websocket', 'polling'],
                auth: { token },
            });
        } catch (error) {
            console.error('Failed to connect socket:', error);
            throw error;
        }

        this.socket.on('connect', () => {
            console.log('Unread checker connected to socket server');
        });

        this.socket.on('disconnect', () => {
            console.log('Unread checker disconnected from socket server');
        });

        this.socket.on('connect_error', (error) => {
            console.error('Socket connection error:', error);
        });

        // 未読通知イベントを受信
        this.socket.on('chat:unread:notify', (data) => {
            this.handleUnreadNotification(data);
        });
    }

    /**
     * Socket通知を受信して処理
     * @param {Object} data - { hasUnread: boolean, timestamp: number }
     */
    handleUnreadNotification(data) {
        console.log('Received unread notification:', data);
        const hasUnread = data?.hasUnread === true;
        this.updateUnreadStatus(hasUnread);
    }

    /**
     * ページロード時の初期未読状態を取得（Laravel DBから）
     */
    async checkUnreadStatus() {
        console.log('Checking initial chat unread status from DB...');
        try {
            const response = await axios.get(this.unreadCheckUrl, {
                withCredentials: true,
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            const hasUnread = response?.data?.has_unread_chat === true;
            this.updateUnreadStatus(hasUnread);
        } catch (error) {
            // エラーが発生してもUIは更新しない（現状維持）
            console.error('Error checking initial unread status:', error);
        }
    }

    /**
     * 未読状態をDOM要素に反映
     */
    updateUnreadStatus(hasUnread) {
        const messageMenuItems = document.querySelectorAll('.js-chat-notification');

        messageMenuItems.forEach((item) => {
            if (hasUnread) {
                item.dataset.active = '';
            } else {
                delete item.dataset.active;
            }
        });

        console.log(`Unread status updated: ${hasUnread}`);
    }

    /**
     * チェック機能を停止
     */
    stop() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
            console.log('Chat unread checker stopped and disconnected');
        }
    }

    /**
     * 手動で未読状態を再確認（初期状態の再取得 + Socket再接続）
     */
    async refresh() {
        // 現在の状態を再取得
        await this.checkUnreadStatus();
        
        // Socket接続が切れていれば再接続
        if (!this.socket || !this.socket.connected) {
            await this.connectSocket();
        }
    }
}

// シングルトンインスタンス
let instance = null;

/**
 * 未読チェッカーを初期化
 * @param {string} socketUrl - Node.jsソケットサーバーのURL
 * @param {string} unreadCheckUrl - 未読状態チェックAPIのURL（デフォルト: /api/user/unread-status）
 */
export const initChatUnreadChecker = (
    socketUrl = socketConfig.baseUrl,
    unreadCheckUrl = appConfig.path.unreadStatus
) => {
    console.log('Initializing ChatUnreadChecker with:', socketUrl, unreadCheckUrl);
    if (!instance) {
        instance = new ChatUnreadChecker(socketUrl, unreadCheckUrl);
        instance.start();
    }
    return instance;
};

/**
 * 未読チェッカーを取得
 */
export const getChatUnreadChecker = () => {
    return instance;
};

export default initChatUnreadChecker;
